// React context for the active role.
//
// Separated from the provider so the provider file exports only components
// (satisfies `react-refresh/only-export-components`).

import { createContext, useContext } from "react";

/**
 * Status describes whether a role has been resolved yet.
 *
 * - `resolving`  — we have not yet run the 42.4 → 42.1 → 42.2 resolution
 *                  pipeline; no role decision has been made.
 * - `single`     — the user holds exactly one role and it has been auto-set
 *                  (R5.3, 42.1).
 * - `selected`   — the user holds multiple roles and has picked one (or a
 *                  deep-link `?as=` set it).
 * - `pending`    — multi-role user with no selection yet; picker must be
 *                  shown and admin nav blocked (42.2).
 * - `none`       — session is loaded but `distinct_roles` is empty; neither
 *                  auto-select nor picker applies. Pages will see this and
 *                  render the "no grants" state.
 */
export type RoleStatus = "resolving" | "single" | "selected" | "pending" | "none";

export interface RoleContextValue {
  /** The resolved active role, or `null` when none has been chosen yet. */
  currentRole: string | null;
  /** Resolution status; drives the picker/switcher visibility logic. */
  status: RoleStatus;
  /**
   * List of distinct roles the user holds (echoed from `session.distinct_roles`
   * for convenience; consumers should prefer this to re-reading the session).
   */
  distinctRoles: readonly string[];
  /**
   * Set the active role. Validates membership in `distinctRoles`; no-op on a
   * role the user does not hold. Bumps `scopedFetchToken` so subscribers can
   * re-fetch without a page reload (42.3).
   */
  setRole: (role: string) => void;
  /**
   * Force the role picker open, regardless of current selection. Used by
   * the api error dispatcher when the backend returns `400 role_required`
   * or `400 invalid_role` — the already-stored role is stale/invalid and
   * the operator must re-pick.
   *
   * When `distinctRoles` is empty, this is a no-op (there is nothing to
   * pick); the caller is expected to surface a different message in that
   * case.
   *
   * Returns `true` if the picker was opened, `false` if suppressed
   * (empty distinct_roles). Lets callers decide whether to toast.
   */
  forcePickerOpen: () => boolean;
  /**
   * Monotonically increasing token incremented on every successful role
   * change. Downstream pages use it as a dep in their data-fetching effect
   * (or as a react-query key suffix) to trigger a refetch.
   */
  scopedFetchToken: number;
}

export const RoleContext = createContext<RoleContextValue | undefined>(undefined);

export function useRole(): RoleContextValue {
  const ctx = useContext(RoleContext);
  if (!ctx) {
    throw new Error("useRole must be used inside <RoleProvider>");
  }
  return ctx;
}
